// src/components/rbac/RolePermissionManager.jsx
import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_ROLES,
  GET_PERMISSIONS,
  GET_ROLE_PERMISSIONS,
  ADD_ROLE_PERMISSION,
  DELETE_ROLE_PERMISSION,
} from "../../graphql/rbac";

export default function RolePermissionManager() {
  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES, {
    fetchPolicy: "network-only",
  });
  const { data: permsData, loading: permsLoading } = useQuery(GET_PERMISSIONS, {
    fetchPolicy: "network-only",
  });

  const [selectedRole, setSelectedRole] = useState(null);

  // Correct: use object destructuring (useQuery returns an object)
  const {
    data: rolePermsData,
    loading: rolePermsLoading,
    refetch: refetchRolePerms,
  } = useQuery(GET_ROLE_PERMISSIONS, {
    fetchPolicy: "network-only",
    skip: !selectedRole,
    variables: { data: { roleId: selectedRole ? selectedRole.id : null } },
  });

  const [addRolePerm] = useMutation(ADD_ROLE_PERMISSION);
  const [deleteRolePerm] = useMutation(DELETE_ROLE_PERMISSION);

  const roles = rolesData?.getRoles?.roles || [];
  const perms = permsData?.getPermissions?.permissions || [];
  const assignedPerms = rolePermsData?.getRolePermissions?.permissions || [];

  useEffect(() => {
    if (!selectedRole && roles.length > 0) setSelectedRole(roles[0]);
  }, [roles, selectedRole]);

  const handleAssign = async (permissionId) => {
    if (!selectedRole) return alert("Select a role first");
    try {
      const res = await addRolePerm({
        variables: { data: { roleId: selectedRole.id, permissionId } },
      });
      if (res?.data?.addRolePermission?.status === "SUCCESS") {
        await refetchRolePerms?.();
      } else {
        alert(
          res?.data?.addRolePermission?.message || "Failed to assign permission"
        );
      }
    } catch (err) {
      console.error(err);
      alert("Error while assigning permission");
    }
  };

  const handleRemove = async (permissionId) => {
    if (!selectedRole) return;
    // using browser confirm as requested
    const ok = window.confirm("Remove permission from role?");
    if (!ok) return;
    try {
      const res = await deleteRolePerm({
        variables: { data: { roleId: selectedRole.id, permissionId } },
      });
      if (res?.data?.deleteRolePermission?.status === "SUCCESS") {
        await refetchRolePerms?.();
      } else {
        alert(
          res?.data?.deleteRolePermission?.message ||
            "Failed to remove permission"
        );
      }
    } catch (err) {
      console.error(err);
      alert("Error while removing permission");
    }
  };

  if (rolesLoading || permsLoading)
    return <p className="text-gray-400">Loading...</p>;

  return (
    <div className="text-white">
      <div className="mb-4">
        <label className="block mb-2">Select Role</label>
        <select
          className="p-2 bg-[#0c0b0b] rounded w-1/3"
          value={selectedRole?.id || ""}
          onChange={(e) => {
            const r = roles.find((x) => x.id === e.target.value);
            setSelectedRole(r || null);
          }}
        >
          <option value="">-- Select role --</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedRole ? (
        <p className="text-gray-400">Select a role to manage permissions</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Assigned Permissions</h4>
              {rolePermsLoading ? (
                <p className="text-gray-400">Loading assigned permissions...</p>
              ) : assignedPerms.length === 0 ? (
                <p className="text-gray-400">No permissions assigned</p>
              ) : (
                <div className="space-y-2">
                  {assignedPerms.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between bg-[#0c0b0b] p-3 rounded"
                    >
                      <div>
                        <div className="font-semibold text-[#FBB034]">
                          {p.code}
                        </div>
                        <div className="text-sm text-gray-300">
                          {p.description}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemove(p.id)}
                        className="text-red-500 px-3 py-1 rounded border border-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="font-semibold mb-2">All Permissions</h4>
              <div className="space-y-2">
                {perms.map((p) => {
                  const isAssigned = assignedPerms.some((ap) => ap.id === p.id);
                  return (
                    <div
                      key={p.id}
                      className="flex items-center justify-between bg-[#0c0b0b] p-3 rounded"
                    >
                      <div>
                        <div className="font-semibold text-[#FBB034]">
                          {p.code}
                        </div>
                        <div className="text-sm text-gray-300">
                          {p.description}
                        </div>
                      </div>
                      <div>
                        {isAssigned ? (
                          <span className="text-sm text-gray-400">
                            Assigned
                          </span>
                        ) : (
                          <button
                            onClick={() => handleAssign(p.id)}
                            className="bg-[#FBB034] px-3 py-1 rounded font-semibold"
                          >
                            Assign
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
